import { FC, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Typography, Box, Divider } from '@mui/material';
import { Form, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { PartnersEnum } from 'entities/Partners/enums';
import { PageHeader } from 'shared/ui/PageHeader';
import { ColorPalette } from 'shared/consts/colorPalette';
import { patchCustomModel } from 'entities/CustomModel/api';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { useCustomModelStore } from 'widgets/customModel/state';
import { Paper } from 'shared/ui/Paper';
import { InputField } from 'shared/ui/InputField';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { SelectOption } from 'shared/ui/Select/types';
import { Button } from 'shared/ui/Button';

const settingsSchema = yup.object({
	name: yup.string().required('is required'),
	settings: yup.object({
		partnerType: yup.mixed<PartnersEnum>().oneOf(Object.values(PartnersEnum)),
	}),
});

const Settings: FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { modelId } = useParams();
	const { t } = useTranslation();
	const { customModels } = useCustomModelStore();

	const currentCustomModel = useMemo(
		() => customModels?.find((item) => item._id === modelId),
		[customModels, modelId],
	);

	const { mutate, isLoading } = useMutation({
		mutationKey: ['post/files/settings'],
		mutationFn: patchCustomModel,
		onSuccess: async () => {
			await queryClient.refetchQueries({ queryKey: ['get/custom-models'] });
			enqueueSnackbar({ message: t('shared_texts.success_update'), variant: 'success' });
			navigate(-1);
		},
		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({ message: errorMessage, variant: 'error' });
		},
	});

	const handleSubmit = (data: yup.InferType<typeof settingsSchema>) => {
		if (modelId) {
			const payload = {
				...data,
				settings: {
					partnerType: data.settings.partnerType || undefined,
				},
			};
			mutate({ customModelId: modelId, data: payload });
		}
	};

	useEffect(() => {
		queryClient.refetchQueries({ queryKey: ['get/custom-models'] });
	}, []);

	return (
		<>
			<PageHeader
				title={t('custom_model.settings.title')}
				description={t('custom_model.settings.description')}
			/>
			<Box
				sx={{
					'@media screen and (min-width: 1441px)': {
						width: '50%',
					},
				}}
			>
				<Formik
					initialValues={{
						name: currentCustomModel?.name || '',
						settings: {
							partnerType: currentCustomModel?.settings?.partnerType || PartnersEnum.default,
						},
					}}
					validationSchema={settingsSchema}
					onSubmit={handleSubmit}
				>
					{({ values, setFieldValue }) => (
						<Form>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
								<Paper
									sx={{
										width: '100%',
										padding: 4,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
									}}
								>
									<Typography
										variant="body1"
										color={ColorPalette.astronaut}
										sx={{ flex: '1 1' }}
									>
										{t('custom_model.create_edit_modal.label')}
									</Typography>
									<InputField
										name={'name'}
										label={''}
										placeholder={
											t('custom_model.create_edit_modal.placeholder') as string
										}
										sx={{ flex: '1 1', margin: '0' }}
										sxError={{ position: 'initial' }}
										required
									/>
								</Paper>

								<Paper sx={{ width: '100%', padding: 4 }}>
									<Typography variant="h5" color={ColorPalette.astronaut}>
										{t('custom_model.settings.partner_title')}
									</Typography>

									<Divider
										color={ColorPalette.periwinkle}
										sx={{ marginY: 2, borderColor: 'transparent' }}
									/>

									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: 4,
										}}
									>
										<Typography
											variant="body1"
											color={ColorPalette.astronaut}
											sx={{ flex: '1 1' }}
										>
											{t('custom_model.settings.partner_description')}
										</Typography>
										<FormSelect
											name={'settings.partnerType'}
											label={''}
											placeholder={t('partners.placeholder.client_supplier')}
											options={selectOptions}
											value={values.settings.partnerType}
											onSelect={(value) => setFieldValue('settings.partnerType', value)}
											sx={{ flex: '1 1', marginBottom: 0 }}
											sxError={{ margin: 0 }}
										/>
									</Box>
								</Paper>

								<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
									<Button
										loading={isLoading}
										onClick={() => navigate(-1)}
										size={'small'}
										variant="outlined"
									>
										{t('shared_texts.cancel')}
									</Button>
									<Button loading={isLoading} size={'small'} type={'submit'}>
										{t('shared_texts.save')}
									</Button>
								</Box>
							</Box>
						</Form>
					)}
				</Formik>
			</Box>
		</>
	);
};

export default Settings;

const selectOptions: Array<SelectOption> = [
	{
		value: undefined,
		label: '-',
	},
	{
		value: PartnersEnum.client,
		label: 'partners.tabs.clients',
	},
	{
		value: PartnersEnum.supplier,
		label: 'partners.tabs.suppliers',
	},
];
